// render-creature-status-form.ts — client-side reconstruction of
// options/tools/statuses/forms/form_modify_creature_status_effects_inc.php (the "edit status effects"
// panel: add a status/ability + manage the creature's current statuses). PHP -> cache -> TS render:
// the creature name + its current-status list come from casterCache, the caster roster comes from the
// cached encounterArr (encounterCache.encounter_creatures_arr) — NO query for any of that. The ONLY
// server touch is the two dynamic catalog pickers (add status / add ability), injected lazily from
// form_add_status_pickers_inc.php (render_dynamic_select can't read a catalog from the caster cache).
// Mutations (add / edit / delete) reuse the existing template.js functions — ping only on click.

import type { CasterBlob, StatusEntry, EditCtx } from './types';
import { str } from './read';
import { iconSpan, iconHtml } from './dom';
import { fillStatusList as fillSharedStatusList } from './status-row';
import { hasPlayerPermission, hasCampaignPermission } from '../shared/array-types';

const PICKERS_PLACEHOLDER_ID = 'ft_add_status_pickers';

function getCaster(id: string): CasterBlob | undefined {
    return id ? (window.FateTide?.casterCache?.get(id) as CasterBlob | undefined) : undefined;
}

function casterName(caster: CasterBlob | undefined): string {
    return caster
        ? (str(caster.encounter_creatures?.encounter_creature_name)
            || str(caster.player_characters?.player_name)
            || str(caster.items?.item_name)
            || '')
        : '';
}

interface EncounterRosterBlob {
    encounter_creatures_arr?: unknown;
}

interface EncounterRosterRow {
    encounter_creatures?: { encounter_creature_id?: unknown; encounter_creature_name?: unknown };
    player_characters?: { player_name?: unknown };
}

function encounterRosterRow(value: unknown): EncounterRosterRow | undefined {
    return value !== null && typeof value === 'object' && !Array.isArray(value)
        ? value as EncounterRosterRow
        : undefined;
}

function encounterRosterBlob(value: unknown): EncounterRosterBlob | undefined {
    return value !== null && typeof value === 'object' && !Array.isArray(value)
        ? value as EncounterRosterBlob
        : undefined;
}

/** Map an encounter blob's roster to {id,name} — this encounter's creatures (ourselves included). */
function readRoster(enc: EncounterRosterBlob | undefined): Array<{ id: string; name: string }> {
    const list = Array.isArray(enc?.encounter_creatures_arr) ? enc.encounter_creatures_arr : [];
    return list.map(encounterRosterRow).filter((ec): ec is EncounterRosterRow => ec !== undefined).map((ec) => ({
        id: str(ec.encounter_creatures?.encounter_creature_id),
        name: str(ec.player_characters?.player_name) || str(ec.encounter_creatures?.encounter_creature_name),
    })).filter((o) => o.id !== '');
}

/** Roster from the WARM encounter cache only — synchronous, never blocks the form render. */
function warmRoster(encounterId: string): Array<{ id: string; name: string }> {
    const enc = encounterId ? window.FateTide?.encounterCache?.get(encounterId) : undefined;
    return readRoster(encounterRosterBlob(enc));
}

/** Roster via getEncounterArr (cache-or-FETCH — can be slow), so ONLY in the async tail. */
async function coldRoster(encounterId: string): Promise<Array<{ id: string; name: string }>> {
    if (!encounterId || typeof getEncounterArr !== 'function') return [];
    try { return readRoster(encounterRosterBlob(await getEncounterArr(encounterId))); } catch { return []; }
}

/** Append a {id,name} option to a native <select>. */
function addRosterOption(sel: HTMLSelectElement, o: { id: string; name: string }): void {
    const opt = document.createElement('option');
    opt.value = o.id;
    opt.textContent = o.name || o.id;
    sel.appendChild(opt);
}

/**
 * A quick-duration button that fills the duration inputs via the existing JS helper. Pass `html` for an
 * icon button (e.g. the permanent glyph) — `label` then becomes the hover title.
 */
function durButton(label: string, durInt: number, durType: string, html?: string): HTMLButtonElement {
    const b = document.createElement('button');
    b.type = 'button';
    b.className = 'btn btn-sm btn-light border border-secondary add-status-quick-dur-btn shadow-none';
    b.setAttribute('data-dur-type', durType);
    if (durType !== 'permanent') b.setAttribute('data-dur-int', String(durInt));
    if (html) { b.innerHTML = html; b.title = label; } else { b.textContent = label; }
    b.addEventListener('click', () => { if (typeof setAddStatusDurationQuick === 'function') setAddStatusDurationQuick(durInt, durType); });
    return b;
}

/**
 * Fill the current-status list container from the caster's `target` bucket (statuses applied TO this
 * creature) via the SHARED status-row renderer (status-row.ts) — the same rows the inventory form
 * draws. Its saved-id patcher also handles refreshes: after a delete / edit the WS caster_update
 * refreshes the cache, then ftRerenderCreatureStatusList() reconciles just the changed rows. NOT
 * the `caster` bucket (those are statuses this creature cast on OTHERS — deleting from that list would
 * hit the wrong creature, the Harrier Picard bug, 2026-06-14).
 */
function fillStatusList(listWrap: HTMLElement, caster: CasterBlob | undefined, ctx: EditCtx): void {
    const perms = { canEdit: hasPlayerPermission(caster), canCampaign: hasCampaignPermission(caster) };
    fillSharedStatusList(listWrap, (caster?.statuses?.target ?? []) as StatusEntry[], ctx, perms);
}

/**
 * Render the "edit creature status effects" panel into `targetId` (default the sidelog form area) from
 * casterCache + encounterCache. Injects the two catalog pickers from a small lazy server fragment, then
 * initializes tom-select. Permission is gated UPSTREAM (the button only appears for permitted viewers)
 * and re-validated by the mutation endpoints.
 */
export async function renderCreatureStatusForm(casterId: string, targetId = 'div_refresh_sidelog_form'): Promise<void> {
    const target = document.getElementById(targetId);
    if (!target || !casterId) return;

    const caster = getCaster(casterId);
    const name = casterName(caster);
    const ctx = buildEditCtx(caster, casterId);
    const encounterId = ctx.encounterId;

    const wrap = document.createElement('div');

    // Header
    const head = document.createElement('div');
    head.className = 'mb-2 border-bottom';
    head.dataset.ftStatusFormIdentity = 'creature';
    head.dataset.ftStatusEntityId = casterId;
    const h5 = document.createElement('h5'); h5.className = 'pb-0 mb-0'; h5.textContent = 'Modify Creature Status Effects';
    const h6 = document.createElement('h6'); h6.className = 'p-2 m-0'; h6.textContent = name;
    h5.dataset.ftStatusFormTitle = '';
    h6.dataset.ftStatusFormName = '';
    head.appendChild(h5); head.appendChild(h6);
    wrap.appendChild(head);

    // Catalog pickers placeholder (filled by the lazy server fragment below). mb-3 so the bordered
    // Quick Duration group (with its on-border label) isn't tight against the add-ability picker.
    const pickers = document.createElement('div');
    pickers.id = PICKERS_PLACEHOLDER_ID;
    pickers.className = 'mb-3';
    wrap.appendChild(pickers);

    // Quick Duration — bordered preset group with the header sitting ON the top border (centered,
    // modernized: uppercase + letter-spacing). Presets least→greatest, Permanent icon last.
    const durBlock = document.createElement('div');
    durBlock.className = 'mb-2';
    const durGroup = document.createElement('div');
    durGroup.className = 'border rounded position-relative px-2 pt-3 pb-2';
    const durLabel = document.createElement('span');
    durLabel.className = 'position-absolute top-0 start-50 translate-middle px-2 bg-body text-secondary fw-semibold';
    durLabel.style.fontSize = '0.68rem';
    durLabel.style.letterSpacing = '0.09em';
    durLabel.style.textTransform = 'uppercase';
    durLabel.style.whiteSpace = 'nowrap';
    durLabel.textContent = 'Quick Duration';
    durGroup.appendChild(durLabel);
    const durRow = document.createElement('div');
    durRow.className = 'd-flex flex-wrap align-items-center justify-content-center';
    durRow.style.gap = '0.35rem';
    durRow.appendChild(durButton('1 min', 1, 'minutes'));
    durRow.appendChild(durButton('10 min', 10, 'minutes'));
    durRow.appendChild(durButton('1 hr', 1, 'hours'));
    durRow.appendChild(durButton('1 day', 1, 'days'));
    durRow.appendChild(durButton('Permanent', 1, 'permanent', iconHtml('status_effect_permanent') || '∞'));
    durGroup.appendChild(durRow);
    durBlock.appendChild(durGroup);

    const durInputs = document.createElement('div');
    durInputs.className = 'd-flex align-items-center mt-1';
    durInputs.style.gap = '0.5rem';
    const intInput = document.createElement('input');
    intInput.type = 'number'; intInput.className = 'form-control form-control-sm'; intInput.id = 'add_status_duration_int'; intInput.placeholder = 'Amount'; intInput.value = '1';
    intInput.style.maxWidth = '80px';
    const typeSel = document.createElement('select'); typeSel.id = 'add_status_duration_type'; typeSel.className = 'ts-select'; typeSel.title = 'Duration Type';
    ([['', '-'], ['permanent', 'permanent'], ['seconds', 'seconds'], ['rounds', 'rounds'], ['minutes', 'minutes'], ['hours', 'hours'], ['days', 'days'], ['weeks', 'weeks'], ['months', 'months'], ['years', 'years']] as const).forEach(([v, l]) => {
        const o = document.createElement('option'); o.value = v; o.textContent = l; if (v === 'permanent') o.selected = true; typeSel.appendChild(o);
    });
    durInputs.appendChild(intInput);
    durInputs.appendChild(typeSel);
    durBlock.appendChild(durInputs);
    wrap.appendChild(durBlock);

    // Caster roster select — from the cached encounterArr (no query).
    const casterRow = document.createElement('div'); casterRow.className = 'col-12 mb-4 d-flex justify-content-start align-items-center';
    const casterIcon = document.createElement('div'); casterIcon.style.width = '50px'; casterIcon.appendChild(iconSpan('caster'));
    const casterSelCol = document.createElement('div');
    const casterSel = document.createElement('select'); casterSel.id = 'add_status_caster_id'; casterSel.className = 'ts-select'; casterSel.title = 'Caster';
    const blank = document.createElement('option'); blank.value = ''; blank.textContent = '-'; casterSel.appendChild(blank);
    // Casters are scoped to THIS encounter (ourselves included). Warm cache fills it instantly here; a cold
    // cache is filled by the async tail below (so a slow getEncounterArr never freezes the panel).
    warmRoster(encounterId).forEach((o) => addRosterOption(casterSel, o));
    casterSelCol.appendChild(casterSel);
    casterRow.appendChild(casterIcon); casterRow.appendChild(casterSelCol);
    wrap.appendChild(casterRow);

    // Add button
    const addRow = document.createElement('div'); addRow.className = 'pt-2 pb-2 mt-2 mb-2';
    const addBtn = document.createElement('button'); addBtn.className = 'btn btn-primary';
    addBtn.innerHTML = `${(() => { try { return iconMap('update'); } catch { return ''; } })()} Add Status Effect to Creature`;
    addBtn.addEventListener('click', () => { if (typeof addStatusEffect === 'function') addStatusEffect(casterId); });
    addRow.appendChild(addBtn);
    wrap.appendChild(addRow);

    wrap.appendChild(document.createElement('hr'));

    // Current statuses applied TO this creature. The list lives in its own container, keyed by caster
    // id, so a delete / edit can repaint JUST this list (off the WS-refreshed cache) without rebuilding
    // the add controls + tom-select pickers above — see ftRerenderCreatureStatusList below.
    const listWrap = document.createElement('div');
    listWrap.setAttribute('data-ft-creature-status-list', casterId);
    fillStatusList(listWrap, caster, ctx);
    wrap.appendChild(listWrap);

    target.innerHTML = '';
    target.appendChild(wrap); // render the form IMMEDIATELY — never block on getEncounterArr (it can be slow).

    // Async tail (NOT awaited): inject the catalog pickers, fill the roster on a cold cache, then init
    // tom-select. The form is already on screen, so a slow getEncounterArr just delays the dropdowns
    // filling in — it no longer freezes the whole panel.
    void (async () => {
        if (typeof queryData === 'function') {
            try { await queryData(`${BASE_URL}options/tools/statuses/forms/form_add_status_pickers_inc.php`, PICKERS_PLACEHOLDER_ID, {}); } catch { /* leave placeholder empty */ }
        }
        if (casterSel.options.length <= 1) {
            (await coldRoster(encounterId)).forEach((o) => addRosterOption(casterSel, o));
        }
        if (typeof initializeTomSelect === 'function') initializeTomSelect();
    })();
}

/**
 * Build the EditCtx (campaign / encounter / caster ids) for a cached caster blob.
 *
 * CAMPAIGN ID (fixed 2026-07-28): this used to read `encounter_creatures.campaign_id` /
 * `player_characters.campaign_id` — NEITHER key exists on a caster blob (encounter_creatures has no
 * campaign column at all; the player-character key is `attached_campaign_id`). It therefore
 * resolved to '' on every blob, and the row/sub-form Pause·Resume gates — which require a campaign
 * id, as their PHP twins do — could never fire. Mirror the PHP precedence instead: the explicit
 * page campaign first (window.ftGetCampaignId(), which IS what pause/resumeStatusEffect POST),
 * then the caster's own attached campaign.
 */
function buildEditCtx(caster: CasterBlob | undefined, casterId: string): EditCtx {
    let pageCampaignId = '';
    try { pageCampaignId = str(window.ftGetCampaignId?.()); } catch { /* template.js not loaded yet */ }
    return {
        statusId: '',
        campaignId: pageCampaignId || str(caster?.player_characters?.attached_campaign_id),
        encounterId: str(caster?.encounter_creatures?.encounter_id),
        casterId,
    };
}

/**
 * Repaint the current-status LIST of an OPEN "Modify Creature Status Effects" form from the freshly
 * cached blob. Wired into the caster_update side effects (caster-update.ts) so that deleting or
 * editing a status — which mutates the creature and pushes a caster_update — drops/updates its row in
 * every mounted list, including side-info and the form. No-op when no list is open for this caster
 * (e.g. the single-status edit sub-form is showing, or the sidelog is closed). The list and its side-info
 * name are refreshed; add-status pickers / duration / caster select keep any in-progress input.
 */
export function ftRerenderCreatureStatusList(casterId: string): void {
    if (!casterId) return;
    const safe = String(casterId).replace(/["\\]/g, '');
    const caster = getCaster(casterId);
    if (!caster) return;
    document.querySelectorAll<HTMLElement>(`[data-ft-creature-status-label="${safe}"]`).forEach(label => {
        const name = casterName(caster);
        if (label.textContent !== name) label.textContent = name;
    });
    document.querySelectorAll<HTMLElement>(`[data-ft-creature-status-list="${safe}"]`).forEach(listWrap => {
        fillStatusList(listWrap, caster, buildEditCtx(caster, casterId));
    });
}
